import {
  type AnalysisApiResponse,
  AnalysisCalculation,
  AnalysisType,
  ConsumptionAnalysisKind,
  type SerializedAnalysisApiResponse,
  Source,
} from '@enmon/commons/lib/enmon';
import type { Serialized } from '@enmon/commons/lib/shared';
import type { LicensesFilter, MeterReadingsFilter } from '../../../types/backendGateway/analysisApi';
import { BaseApiClient } from '../baseApiClient';

export type GetLicensesAnalysisArg = LicensesFilter;
export type GetLicensesAnalysisResponse = AnalysisApiResponse<Serialized>;

export class AnalysisApiClient extends BaseApiClient {
  constructor(jwt: string) {
    super('enmon-tools/analysis', undefined, jwt);
  }

  public async getReadings(
    filter: MeterReadingsFilter,
    signal: AbortSignal | undefined,
  ): Promise<SerializedAnalysisApiResponse> {
    const [counters, values] = await Promise.all([
      this.axios.get<SerializedAnalysisApiResponse>(`${AnalysisType.Consumption}/${ConsumptionAnalysisKind.Counter}`, {
        params: filter,
        signal,
      }),
      this.axios.get<SerializedAnalysisApiResponse>(AnalysisType.Values, {
        params: { ...filter, calculation: AnalysisCalculation.Average },
        signal,
      }),
    ]);

    return {
      // Both analyses use the same time buckets for the same filters.
      categories: counters.data.categories,
      series: [
        ...counters.data.series,
        ...values.data.series.map(serie => ({
          ...serie,
          meta: { ...serie.meta, source: Source.Sensor }
        })),
      ],
    };
  }

  public async dataIntegrity(
    filter: Record<string, any>,
    signal: AbortSignal | undefined,
  ): Promise<SerializedAnalysisApiResponse> {
    const params = { ...filter };

    const response = await this.axios.get<SerializedAnalysisApiResponse>(`${AnalysisType.DataIntegrity}`, {
      params,
      signal,
    });

    return response.data;
  }

  public async getLicenses(
    filter: GetLicensesAnalysisArg,
    signal: AbortSignal | undefined,
  ): Promise<GetLicensesAnalysisResponse> {
    const { granularity, period, customers, depth } = filter;
    const params = new URLSearchParams();

    if (granularity) {
      params.append('granularity', granularity);
    }

    if (period) {
      params.append('period', period);
    }

    if (customers) {
      params.append('customers', customers.join(','));
    }

    if (depth) {
      params.append('depth', depth.toString());
    }

    const response = await this.axios.get<GetLicensesAnalysisResponse>(`/${AnalysisType.Licenses}`, {
      params: params.toString(),
      signal,
    });

    return response.data;
  }
}
